import {
  doc,
  onSnapshot,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  limit,
  orderBy,
  arrayUnion,
  Timestamp,
  deleteField,
  getFirestore,
} from "firebase/firestore";
import { db } from "../config/FirebaseConfig.js"; // Import the Firebase setup

export const convertSecondsToFirestoreTimestamp = (seconds) => {
  // Firestore expects milliseconds, so convert seconds to milliseconds
  const milliseconds = seconds * 1000;

  // Create and return a Firestore Timestamp
  return Timestamp.fromMillis(milliseconds);
};

export async function getDocDataById(collection, id) {
  try {
    if (!collection || !id) {
      console.error("Collection or ID is missing");
      return null;
    }
    const docRef = doc(db, collection, id);
    const docSnapshot = await getDoc(docRef);
    if (docSnapshot.exists()) {
      return { id: id, ref: docRef, ...docSnapshot.data() }; // Return plain object with document reference and ID
    }
    return null;
  } catch (error) {
    console.error("Error fetching document:", error);
    return null;
  }
}

export async function getDocDataByRef(docRef) {
  try {
    const docSnapshot = await getDoc(docRef);
    if (docSnapshot.exists()) {
      return { id: docSnapshot.id, ref: docRef, ...docSnapshot.data() }; // Return plain object with document reference and ID
    }
    return null; // Return null if no document exists
  } catch (error) {
    console.error("Error fetching document:", error);
    return null;
  }
}

export function listenDocDataByRef(docRef, callback) {
  const unsubscribe = onSnapshot(
    docRef,
    (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = { id: docSnapshot.id, ref: docRef, ...docSnapshot.data() };
        callback(data);
      } else {
      }
    },
    (error) => {
      console.error("Error fetching folder:", error);
    }
  );

  return unsubscribe;
}

export function listenDocDataByRefX(docRef, callback, unsubscribeRef) {
  try {
    // Ensure we unsubscribe from the previous listener before creating a new one
    if (unsubscribeRef.current) {
      unsubscribeRef.current(); // Stop the old listener
    }

    // Set up a new listener
    unsubscribeRef.current = onSnapshot(
      docRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = {
            id: docSnapshot.id,
            ref: docRef,
            ...docSnapshot.data(),
          };
          callback(data);
        }
      },
      (error) => {
        console.error("Error fetching document:", error);
      }
    );

    // Return a cleanup function to stop listening when needed
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  } catch (error) {
    console.error("Error setting up snapshot listener:", error);
  }
}

export function listenDocDataById(collection, id, callback) {
  try {
    const docRef = doc(db, collection, id);
    return listenDocDataByRef(docRef, callback);
  } catch (error) {
    console.error("Error listening to document:", error);
  }
}

export function listenDocDataByIdX(collection, id, callback, unsubscribeRef) {
  try {
    const docRef = doc(db, collection, id);
    return listenDocDataByRefX(docRef, callback, unsubscribeRef);
  } catch (error) {
    console.error("Error listening to document:", error);
  }
}

export async function createDoc(collectionName, data) {
  try {
    // Create a reference to the collection
    const docRef = doc(collection(db, collectionName)); // Automatically generates a document ID
    // Write the document
    await setDoc(docRef, data);
    // Fetch the newly created document
    const docSnapshot = await getDoc(docRef);
    return { id: docSnapshot.id, ref: docRef, ...docSnapshot.data() }; // Return the document's data with its ID and reference
  } catch (error) {
    console.error("Error creating document:", error);
    return null;
  }
}

export async function deleteDocById(collection, id) {
  try {
    const docRef = doc(db, collection, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting document by ID:", error);
  }
}

export function last_of_list(list) {
  return list[list.length - 1];
}

export async function deleteDocByRef(docRef) {
  try {
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting document by reference:", error);
  }
}

export async function getArrayDocRefByIDs(collection, ids) {
  try {
    if (!ids) {
      console.error("List of document IDs is missing");
      return [];
    }
    const docRefs = ids.map((id) => getRefFromId(collection, id));
    return docRefs;
  } catch (error) {
    console.error("Error getting array of document references by IDs:", error);
    return [];
  }
}

export async function generateDocumentsXFromDocumentsXX(documentsXXRefs) {
  try {
    if (!documentsXXRefs) {
      console.error("List of documentXX references is missing");
      return [];
    }
    const documentsX = [];
    for (const docXXRef of documentsXXRefs) {
      const docXXData = await getDocDataByRef(docXXRef);
      if (docXXData) {
        const docXData = {
          name: docXXData.name,
          description: docXXData.description,
          custom: false,
          docXX_ref: docXXRef,
        };
        const docX = await createDoc("documentx", docXData);
        documentsX.push(docX);
      }
    }
    return documentsX;
  } catch (error) {
    console.error("Error generating documents X from documents XX:", error);
    return [];
  }
}

export async function generateDocumentsXRefsFromDocumentsXX(documentsXXRefs) {
  try {
    if (!documentsXXRefs) {
      console.error("List of documentXX references is missing");
      return [];
    }
    const documentsXRefs = [];
    for (const docXXRef of documentsXXRefs) {
      const docXXData = await getDocDataByRef(docXXRef);
      if (docXXData) {
        const docXData = {
          name: docXXData.name,
          icon: docXXData.icon,
          infos: docXXData.infos,
          storage_path: null,
          state: "locked",
          documentxx: docXXRef,
        };
        const docX = await createDoc("documentx", docXData);
        documentsXRefs.push(docX.ref);
      }
    }
    return documentsXRefs;
  } catch (error) {
    console.error(
      "Error generating documents X refs from documents XX:",
      error
    );
    return [];
  }
}

export async function getArrayDocDataByRef(listdocRef) {
  try {
    if (!listdocRef) {
      console.error("List of document references is missing");
      return [];
    }
    if (listdocRef?.length === 0) {
      return [];
    }

    // Use Promise.all to fetch all documents concurrently
    const docSnapshots = await Promise.all(
      listdocRef.map((docRef) => getDoc(docRef))
    );
    // Filter and map the results to include only existing documents
    const docs = docSnapshots
      .filter((docSnapshot) => docSnapshot.exists())
      .map((docSnapshot) => ({
        id: docSnapshot.id,
        ref: docSnapshot.ref,
        ...docSnapshot.data(),
      }));

    return docs;
  } catch (error) {
    console.error("Error fetching documents:", error);
    return [];
  }
}

export async function getArrayDocDataByID(collection, listdocID) {
  try {
    if (!listdocID) {
      console.error("List of document references is missing");
      return [];
    }
    if (listdocID?.length === 0) {
      return [];
    }
    let listdocRef = [];
    for (const docID of listdocID) {
      const docRef = doc(db, collection, docID);
      listdocRef.push(docRef);
    }

    // Use Promise.all to fetch all documents concurrently
    const docSnapshots = await Promise.all(
      listdocRef.map((docRef) => getDoc(docRef))
    );
    // Filter and map the results to include only existing documents
    const docs = docSnapshots
      .filter((docSnapshot) => docSnapshot.exists())
      .map((docSnapshot) => ({
        id: docSnapshot.id,
        ref: docSnapshot.ref,
        ...docSnapshot.data(),
      }));

    return docs;
  } catch (error) {
    console.error("Error fetching documents:", error);
    return [];
  }
}

export function getFlutterTime() {
  return Timestamp.now();
}

export function getFlutterTimeFromSeconds(seconds) {
  return Timestamp.fromMillis(seconds * 1000);
}

export function getDateFromFlutter(flutter_date) {
  if (!flutter_date) {
    return null;
  }
  return flutter_date.toDate();
}

export function getDateFromFirebase(start) {
  return new Date(start.seconds * 1000);
}

export function getDateFromSeconds(seconds) {
  return new Date(seconds * 1000);
}
export function getFlutterTimeFromSecondsFormatDate(seconds) {
  // to ("DD/MM/YYYY")
  return getFlutterTimeFromSeconds(seconds)
    .toDate()
    .toLocaleDateString("fr-FR");
}

export function getFlutterTimeFromSecondsFormatTime(seconds) {
  // to ("HH:mm")
  return getFlutterTimeFromSeconds(seconds)
    .toDate()
    .toLocaleTimeString("fr-FR");
  // to ("DD/MM/YYYY")
}

export async function updateDocDataField(docRef, field, value) {
  try {
    await setDoc(docRef, { [field]: value }, { merge: true });
  } catch (error) {
    console.error("Error updating document field:", error);
  }
}

export async function deleteDocDataField(docRef, field) {
  try {
    await setDoc(docRef, { [field]: deleteField() }, { merge: true });
  } catch (error) {
    console.error("Error deleting document field:", error);
  }
}

export async function addToArrayField(docRef, field, item) {
  try {
    await setDoc(
      docRef,
      { [field]: arrayUnion(item) }, // Use arrayUnion to add item to array
      { merge: true } // Ensure existing data is not overwritten
    );
  } catch (error) {
    console.error("Error adding to array field:", error);
  }
}

export function getRefFromId(collection, id) {
  return doc(db, collection, id);
}

export async function getDocumentsByFolderRef(folderRef) {
  try {
    const docSnapshot = await getDoc(folderRef);
    const docData = docSnapshot.data();
    const docs = [];

    for (const docRef of docData?.documents || []) {
      const docSnapshot = await getDoc(docRef);
      if (docSnapshot.exists()) {
        docs.push({
          id: docRef.id,
          ref: docRef,
          ...docSnapshot.data(),
        }); // Include document reference and ID
      }
    }
    return docs || [];
  } catch (error) {
    console.error("Error getting documents by folder reference:", error);
    return [];
  }
}

export function TruncatedString(text, maxLength = 20) {
  const truncatedText =
    text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  return truncatedText;
}

// Dynamic query function
export async function queryCollection(
  collectionName,
  filters,
  limite = 10000,
  sortingField = null,
  sortingOrder = "asc"
) {
  try {
    if (!collectionName) {
      console.error("Collection name is missing");
      return null;
    }

    // Initialize query with the collection reference
    let objectsQuery = collection(db, collectionName);

    // Apply dynamic filters if provided
    if (filters && filters.length > 0) {
      const queries = filters.map((filter) =>
        where(filter.field, filter.operator, filter.value)
      );
      objectsQuery = query(objectsQuery, ...queries);
    }

    // Apply sorting if a sortingField is provided
    if (sortingField) {
      objectsQuery = query(objectsQuery, orderBy(sortingField, sortingOrder));
    }

    // Apply the limit to the query
    objectsQuery = query(objectsQuery, limit(limite));

    // Fetch the documents that match the query
    const querySnapshot = await getDocs(objectsQuery);

    // Map the documents to an array of data with document ID and reference
    const objects = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ref: doc.ref,
      ...doc.data(),
    }));

    return objects || [];
  } catch (error) {
    console.error("Error querying collection:", error);
    return null;
  }
}

// Dynamic query function with active listening
export function listenCollection(
  collectionName,
  filters,
  limite = 1000,
  sortingField = null,
  sortingOrder = "asc",
  onDataChange,
  unsubscribeRef = null,
  onError
) {
  try {
    if (!collectionName) {
      console.error("Collection name is missing");
      return null;
    }

    if (unsubscribeRef?.current) {
      unsubscribeRef.current();
    }

    const db = getFirestore();

    // Initialize query with the collection reference
    let objectsQuery = collection(db, collectionName);

    // Apply dynamic filters if provided
    if (filters && filters.length > 0) {
      const queries = filters.map((filter) =>
        where(filter.field, filter.operator, filter.value)
      );
      objectsQuery = query(objectsQuery, ...queries);
    }

    // Apply sorting if a sortingField is provided
    if (sortingField) {
      objectsQuery = query(objectsQuery, orderBy(sortingField, sortingOrder));
    }

    // Apply the limit to the query
    objectsQuery = query(objectsQuery, limit(limite));

    // Set up a listener for the query
    let current = onSnapshot(
      objectsQuery,
      (querySnapshot) => {
        const objects = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ref: doc.ref,
          ...doc.data(),
        }));

        // Call the onDataChange callback with the updated data
        if (onDataChange) {
          onDataChange(objects);
        }
      },
      (error) => {
        console.error("Error listening to collection:", error);
        if (onError) {
          onError(error);
        }
      }
    );

    if (unsubscribeRef) {
      unsubscribeRef.current = current;
    }

    return () => {
      if (unsubscribeRef?.current) {
        unsubscribeRef.current();
      }
    };
  } catch (error) {
    console.error("Error setting up snapshot listener:", error);
    return null;
  }
}
